import IconButton from "@/components/Button/IconButton";
import { Text } from "@/components/Typography/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { defaultNS, useTranslation } from "@/lib/i18n";

import type { Option, UIState } from "./DynamicSelectField.type";

export const ReadyStatePresentation = ({
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  error,
  placeholder,
  testId,
  options,
  uiState,
  getSelectedOptionLabel,
  hasValue,
  onClear,
}: {
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string | null;
  placeholder?: string;
  testId?: string;
  options: Option[];
  uiState: Extract<UIState, { type: "ready" }>;
  getSelectedOptionLabel: (value: string) => string;
  hasValue?: boolean;
  onClear?: () => void;
}) => {
  const { t } = useTranslation(defaultNS);

  const canClear =
    !!onClear && !!hasValue && !(disabled || readOnly || uiState.isLoading);

  return (
    <Select
      value={value}
      onValueChange={onChange}
      disabled={disabled || readOnly || uiState.isLoading}
      onOpenChange={(open) => {
        if (open && onFocus) onFocus();
        if (!open && onBlur) onBlur();
      }}
    >
      <div className="relative w-full">
        <SelectTrigger
          className={`w-full ${error && "border-error-fill"}`}
          data-testid={testId}
          data-role="form-field-input"
          aria-invalid={!!error}
          disabled={readOnly}
        >
          <div className={`min-w-0 ${canClear ? "pr-12" : ""}`}>
            <SelectValue
              placeholder={
                uiState.isLoading
                  ? t("common.loading")
                  : uiState.placeholder ||
                    placeholder ||
                    t("common.pleaseSelect")
              }
            />
          </div>
        </SelectTrigger>

        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <SelectContent>
        {uiState.isEmpty ? (
          <Text variant="inputLabel" className="px-3 py-2 text-warning-fill">
            {t("common.noOptions")}
          </Text>
        ) : (
          options.map((item) => (
            <SelectItem
              key={item.value}
              value={item.value}
              title={item.description || undefined}
            >
              {getSelectedOptionLabel(item.value)}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};
